import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  IconButton
} from '@mui/material';
import {
  AccessTime,
  EventBusy,
  ScheduleOutlined,
  BeachAccess,
  WorkOff,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';

const EmployeeDataTable = ({ data = {} }) => {
  const [isExpanded, setIsExpanded] = useState(false); // Initially closed

  const {
    months = [],
    personalHours = [],
    personalRecords = [],
    sicknessHours = [],
    sicknessRecords = [],
    latenessHours = [],
    latenessRecords = [],
    unpaidLeaveHours = [],
    unpaidLeaveDays = [],
    annualLeaveHours = [],
    annualLeaveDays = []
  } = data;

  const today = new Date();
const currentMonthIndex = today.getMonth(); // 0 = Jan, 9 = Oct

// Filter months and related arrays
const filteredMonths = months.filter((month, index) => index <= currentMonthIndex);
const filteredPersonalHours = personalHours.slice(0, filteredMonths.length);
const filteredPersonalRecords = personalRecords.slice(0, filteredMonths.length);
const filteredSicknessHours = sicknessHours.slice(0, filteredMonths.length);
const filteredSicknessRecords = sicknessRecords.slice(0, filteredMonths.length);
const filteredLatenessHours = latenessHours.slice(0, filteredMonths.length);
const filteredLatenessRecords = latenessRecords.slice(0, filteredMonths.length);
const filteredUnpaidLeaveHours = unpaidLeaveHours.slice(0, filteredMonths.length);
const filteredUnpaidLeaveDays = unpaidLeaveDays.slice(0, filteredMonths.length);
const filteredAnnualLeaveHours = annualLeaveHours.slice(0, filteredMonths.length);
const filteredAnnualLeaveDays = annualLeaveDays.slice(0, filteredMonths.length);


  const formatHours = (hours) => {
    return hours > 0 ? hours.toFixed(2) : '-';
  };

  const formatRecords = (records) => {
    return records > 0 ? records : '-';
  };

  const getCellColor = (value, type) => {
    if (!value || value === 0) return '#f5f5f5';
    
    switch(type) {
      case 'personal': return '#e3f2fd';
      case 'sickness': return '#fce4ec';
      case 'lateness': return '#fff3e0';
      case 'unpaid': return '#f3e5f5';
      case 'annual': return '#e8f5e9';
      default: return '#f5f5f5';
    }
  };

  // Inside your component, after destructuring `data`
   const totalMonths = months.length;

   // Generate indices in reverse order: [9, 8, 7, ..., 0] (if 10 months)
   const reversedIndices = Array.from({ length: totalMonths }, (_, i) => totalMonths - 1 - i);


  return (
    <Box sx={{ padding: '0px', height: '100%', marginBottom:3 }}>
      <Paper elevation={3} sx={{ borderRadius: '16px', overflow: 'hidden' }}>
        {/* Clickable Header */}
        <Box 
          sx={{ 
            background: 'linear-gradient(135deg, #adbbda 0%, #8697c4 100%)',
            padding: '24px',
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', marginBottom: '8px' }}>
              📊 Attendance KPI
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              {isExpanded 
                ? "Click to collapse details" 
                : "Click to expand monthly breakdown"}
            </Typography>
          </Box>
          <IconButton sx={{ color: 'white' }}>
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        {/* Conditionally Render Table */}
        {isExpanded && (
          <TableContainer sx={{ maxHeight: '80vh' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell 
                    sx={{ 
                      backgroundColor: '#f5f5f5',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      borderRight: '2px solid #e0e0e0',
                      position: 'sticky',
                      left: 0,
                      zIndex: 3
                    }}
                  >
                    Month
                  </TableCell>
                  <TableCell align="center" sx={{ backgroundColor: '#e3f2fd', fontWeight: 'bold', fontSize: '13px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                      <ScheduleOutlined fontSize="small" />
                      Personal Hours
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ backgroundColor: '#e3f2fd', fontWeight: 'bold', fontSize: '13px' }}>
                    Personal Records
                  </TableCell>
                  <TableCell align="center" sx={{ backgroundColor: '#fce4ec', fontWeight: 'bold', fontSize: '13px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                      <EventBusy fontSize="small" />
                      Sickness Hours
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ backgroundColor: '#fce4ec', fontWeight: 'bold', fontSize: '13px' }}>
                    Sickness Records
                  </TableCell>
                  <TableCell align="center" sx={{ backgroundColor: 'var(--warning-light, #fff3e0)', fontWeight: 'bold', fontSize: '13px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                      <AccessTime fontSize="small" />
                      Lateness Hours
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ backgroundColor: 'var(--warning-light, #fff3e0)', fontWeight: 'bold', fontSize: '13px' }}>
                    Lateness Records
                  </TableCell>
                  <TableCell align="center" sx={{ backgroundColor: '#f3e5f5', fontWeight: 'bold', fontSize: '13px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                      <WorkOff fontSize="small" />
                      Unpaid Leave Hours
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ backgroundColor: '#f3e5f5', fontWeight: 'bold', fontSize: '13px' }}>
                    Unpaid Leave Days
                  </TableCell>
                  <TableCell align="center" sx={{ backgroundColor: '#e8f5e9', fontWeight: 'bold', fontSize: '13px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                      <BeachAccess fontSize="small" />
                      Annual Leave Hours
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ backgroundColor: '#e8f5e9', fontWeight: 'bold', fontSize: '13px' }}>
                    Annual Leave Days
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
  {filteredMonths.length > 0 ? (
    Array.from({ length: filteredMonths.length }, (_, i) => filteredMonths.length - 1 - i)
      .map((originalIndex) => (
        <TableRow 
          key={originalIndex}
          sx={{ 
            '&:hover': { backgroundColor: '#f9f9f9' },
            '&:nth-of-type(odd)': { backgroundColor: '#fefefe' }
          }}
        >
          <TableCell 
            component="th" 
            scope="row"
            sx={{ 
              fontWeight: 'bold',
              fontSize: '14px',
              color: '#667eea',
              borderRight: '2px solid #e0e0e0',
              position: 'sticky',
              left: 0,
              backgroundColor: 'inherit',
              zIndex: 1
            }}
          >
            {filteredMonths[originalIndex]}
          </TableCell>
          <TableCell align="center" sx={{ backgroundColor: getCellColor(filteredPersonalHours[originalIndex], 'personal') }}>
            {formatHours(filteredPersonalHours[originalIndex])}
          </TableCell>
          <TableCell align="center" sx={{ backgroundColor: getCellColor(filteredPersonalRecords[originalIndex], 'personal') }}>
            {formatRecords(filteredPersonalRecords[originalIndex])}
          </TableCell>
          <TableCell align="center" sx={{ backgroundColor: getCellColor(filteredSicknessHours[originalIndex], 'sickness') }}>
            {formatHours(filteredSicknessHours[originalIndex])}
          </TableCell>
          <TableCell align="center" sx={{ backgroundColor: getCellColor(filteredSicknessRecords[originalIndex], 'sickness') }}>
            {formatRecords(filteredSicknessRecords[originalIndex])}
          </TableCell>
          <TableCell align="center" sx={{ backgroundColor: getCellColor(filteredLatenessHours[originalIndex], 'lateness') }}>
            {formatHours(filteredLatenessHours[originalIndex])}
          </TableCell>
          <TableCell align="center" sx={{ backgroundColor: getCellColor(filteredLatenessRecords[originalIndex], 'lateness') }}>
            {formatRecords(filteredLatenessRecords[originalIndex])}
          </TableCell>
          <TableCell align="center" sx={{ backgroundColor: getCellColor(filteredUnpaidLeaveHours[originalIndex], 'unpaid') }}>
            {formatHours(filteredUnpaidLeaveHours[originalIndex])}
          </TableCell>
          <TableCell align="center" sx={{ backgroundColor: getCellColor(filteredUnpaidLeaveDays[originalIndex], 'unpaid') }}>
            {formatRecords(filteredUnpaidLeaveDays[originalIndex])}
          </TableCell>
          <TableCell align="center" sx={{ backgroundColor: getCellColor(filteredAnnualLeaveHours[originalIndex], 'annual') }}>
            {formatHours(filteredAnnualLeaveHours[originalIndex])}
          </TableCell>
          <TableCell align="center" sx={{ backgroundColor: getCellColor(filteredAnnualLeaveDays[originalIndex], 'annual') }}>
            {formatRecords(filteredAnnualLeaveDays[originalIndex])}
          </TableCell>
        </TableRow>
      ))
  ) : (
    <TableRow>
      <TableCell colSpan={11} align="center">No data available</TableCell>
    </TableRow>
  )}
</TableBody>

            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default EmployeeDataTable;

// import React from 'react';
// import {
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Paper,
//   Chip,
//   Box,
//   Typography
// } from '@mui/material';
// import {
//   AccessTime,
//   EventBusy,
//   ScheduleOutlined,
//   BeachAccess,
//   WorkOff
// } from '@mui/icons-material';

// const EmployeeDataTable = ({ data = {} }) => {
//   const {
//     months = [],
//     personalHours = [],
//     personalRecords = [],
//     sicknessHours = [],
//     sicknessRecords = [],
//     latenessHours = [],
//     latenessRecords = [],
//     unpaidLeaveHours = [],
//     unpaidLeaveDays = [],
//     annualLeaveHours = [],
//     annualLeaveDays = []
//   } = data;

//   const formatHours = (hours) => {
//     return hours > 0 ? hours.toFixed(2) : '-';
//   };

//   const formatRecords = (records) => {
//     return records > 0 ? records : '-';
//   };

//   const getCellColor = (value, type) => {
//     if (!value || value === 0) return '#f5f5f5';
    
//     switch(type) {
//       case 'personal':
//         return '#e3f2fd';
//       case 'sickness':
//         return '#fce4ec';
//       case 'lateness':
//         return '#fff3e0';
//       case 'unpaid':
//         return '#f3e5f5';
//       case 'annual':
//         return '#e8f5e9';
//       default:
//         return '#f5f5f5';
//     }
//   };

//   return (
//     <Box sx={{ padding: '0px', minHeight: '100vh' }}>
//       <Paper elevation={3} sx={{ borderRadius: '16px', overflow: 'hidden' }}>
//         <Box sx={{ 
//           background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
//           padding: '24px',
//           color: 'white'
//         }}>
//           <Typography variant="h4" sx={{ fontWeight: 'bold', marginBottom: '8px' }}>
//             📊 Attendance KPI
//           </Typography>
//           <Typography variant="body1" sx={{ opacity: 0.9 }}>
//             Monthly breakdown of hours and records
//           </Typography>
//         </Box>

//         <TableContainer sx={{ maxHeight: '80vh' }}>
//           <Table stickyHeader>
//             <TableHead>
//               <TableRow>
//                 <TableCell 
//                   sx={{ 
//                     backgroundColor: '#f5f5f5',
//                     fontWeight: 'bold',
//                     fontSize: '14px',
//                     borderRight: '2px solid #e0e0e0',
//                     position: 'sticky',
//                     left: 0,
//                     zIndex: 3
//                   }}
//                 >
//                   Month
//                 </TableCell>
//                 <TableCell align="center" sx={{ backgroundColor: '#e3f2fd', fontWeight: 'bold', fontSize: '13px' }}>
//                   <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
//                     <ScheduleOutlined fontSize="small" />
//                     Personal Hours
//                   </Box>
//                 </TableCell>
//                 <TableCell align="center" sx={{ backgroundColor: '#e3f2fd', fontWeight: 'bold', fontSize: '13px' }}>
//                   Personal Records
//                 </TableCell>
//                 <TableCell align="center" sx={{ backgroundColor: '#fce4ec', fontWeight: 'bold', fontSize: '13px' }}>
//                   <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
//                     <EventBusy fontSize="small" />
//                     Sickness Hours
//                   </Box>
//                 </TableCell>
//                 <TableCell align="center" sx={{ backgroundColor: '#fce4ec', fontWeight: 'bold', fontSize: '13px' }}>
//                   Sickness Records
//                 </TableCell>
//                 <TableCell align="center" sx={{ backgroundColor: 'var(--warning-light, #fff3e0)', fontWeight: 'bold', fontSize: '13px' }}>
//                   <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
//                     <AccessTime fontSize="small" />
//                     Lateness Hours
//                   </Box>
//                 </TableCell>
//                 <TableCell align="center" sx={{ backgroundColor: 'var(--warning-light, #fff3e0)', fontWeight: 'bold', fontSize: '13px' }}>
//                   Lateness Records
//                 </TableCell>
//                 <TableCell align="center" sx={{ backgroundColor: '#f3e5f5', fontWeight: 'bold', fontSize: '13px' }}>
//                   <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
//                     <WorkOff fontSize="small" />
//                     Unpaid Leave Hours
//                   </Box>
//                 </TableCell>
//                 <TableCell align="center" sx={{ backgroundColor: '#f3e5f5', fontWeight: 'bold', fontSize: '13px' }}>
//                   Unpaid Leave Days
//                 </TableCell>
//                 <TableCell align="center" sx={{ backgroundColor: '#e8f5e9', fontWeight: 'bold', fontSize: '13px' }}>
//                   <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
//                     <BeachAccess fontSize="small" />
//                     Annual Leave Hours
//                   </Box>
//                 </TableCell>
//                 <TableCell align="center" sx={{ backgroundColor: '#e8f5e9', fontWeight: 'bold', fontSize: '13px' }}>
//                   Annual Leave Days
//                 </TableCell>
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {months.map((month, index) => (
//                 <TableRow 
//                   key={index}
//                   sx={{ 
//                     '&:hover': { backgroundColor: '#f9f9f9' },
//                     '&:nth-of-type(odd)': { backgroundColor: '#fefefe' }
//                   }}
//                 >
//                   <TableCell 
//                     component="th" 
//                     scope="row"
//                     sx={{ 
//                       fontWeight: 'bold',
//                       fontSize: '14px',
//                       color: '#667eea',
//                       borderRight: '2px solid #e0e0e0',
//                       position: 'sticky',
//                       left: 0,
//                       backgroundColor: 'inherit',
//                       zIndex: 1
//                     }}
//                   >
//                     {month}
//                   </TableCell>
//                   <TableCell 
//                     align="center"
//                     sx={{ backgroundColor: getCellColor(personalHours[index], 'personal') }}
//                   >
//                     {formatHours(personalHours[index])}
//                   </TableCell>
//                   <TableCell 
//                     align="center"
//                     sx={{ backgroundColor: getCellColor(personalRecords[index], 'personal') }}
//                   >
//                     {formatRecords(personalRecords[index])}
//                   </TableCell>
//                   <TableCell 
//                     align="center"
//                     sx={{ backgroundColor: getCellColor(sicknessHours[index], 'sickness') }}
//                   >
//                     {formatHours(sicknessHours[index])}
//                   </TableCell>
//                   <TableCell 
//                     align="center"
//                     sx={{ backgroundColor: getCellColor(sicknessRecords[index], 'sickness') }}
//                   >
//                     {formatRecords(sicknessRecords[index])}
//                   </TableCell>
//                   <TableCell 
//                     align="center"
//                     sx={{ backgroundColor: getCellColor(latenessHours[index], 'lateness') }}
//                   >
//                     {formatHours(latenessHours[index])}
//                   </TableCell>
//                   <TableCell 
//                     align="center"
//                     sx={{ backgroundColor: getCellColor(latenessRecords[index], 'lateness') }}
//                   >
//                     {formatRecords(latenessRecords[index])}
//                   </TableCell>
//                   <TableCell 
//                     align="center"
//                     sx={{ backgroundColor: getCellColor(unpaidLeaveHours[index], 'unpaid') }}
//                   >
//                     {formatHours(unpaidLeaveHours[index])}
//                   </TableCell>
//                   <TableCell 
//                     align="center"
//                     sx={{ backgroundColor: getCellColor(unpaidLeaveDays[index], 'unpaid') }}
//                   >
//                     {formatRecords(unpaidLeaveDays[index])}
//                   </TableCell>
//                   <TableCell 
//                     align="center"
//                     sx={{ backgroundColor: getCellColor(annualLeaveHours[index], 'annual') }}
//                   >
//                     {formatHours(annualLeaveHours[index])}
//                   </TableCell>
//                   <TableCell 
//                     align="center"
//                     sx={{ backgroundColor: getCellColor(annualLeaveDays[index], 'annual') }}
//                   >
//                     {formatRecords(annualLeaveDays[index])}
//                   </TableCell>
//                 </TableRow>
//               ))}
//             </TableBody>
//           </Table>
//         </TableContainer>
//       </Paper>
//     </Box>
//   );
// };

// export default EmployeeDataTable;